import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Globe, TrendingUp, Users, Package, Handshake, BarChart3 } from "lucide-react";

// Eyebrow component
function Eyebrow({ children }) {
  return <span className="eyebrow">{children}</span>;
}

// Glass card component
function GlassCard({ children, className = "" }) {
  return (
    <motion.div
      className={`glass-card p-6 md:p-8 ${className}`}
      whileHover={{ y: -4, borderColor: 'rgba(86, 205, 236, 0.3)' }}
      transition={{ duration: 0.3 }}
    >
      {children}
    </motion.div>
  );
}

// Stagger reveal
function StaggerReveal({ children, index = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

const benefits = [
  { icon: Globe, title: "Global Reach", desc: "Access our distribution network spanning 10+ countries and 500+ active retailers." },
  { icon: TrendingUp, title: "Volume Sales", desc: "Move large quantities efficiently through our established B2B channels." },
  { icon: Users, title: "Trusted Network", desc: "Your products reach verified, KYC-compliant business clients." },
  { icon: Package, title: "Warehousing", desc: "Leverage our multi-country warehouse infrastructure for regional distribution." },
  { icon: Handshake, title: "Flexible Terms", desc: "Customizable partnership models to suit your business needs." },
  { icon: BarChart3, title: "Market Insights", desc: "Access demand data and market intelligence to optimize your supply." },
];

export default function BecomeSupplier() {
  const heroRef = useRef(null);
  const heroInView = useInView(heroRef, { once: true, amount: 0.3 });

  return (
    <main data-testid="become-supplier-page">
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 relative" ref={heroRef}>
        <div className="absolute inset-0 grid-bg opacity-20" />
        <motion.div
          className="max-w-7xl mx-auto px-fluid text-center relative z-10"
          initial={{ opacity: 0, y: 40 }}
          animate={heroInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <Eyebrow>Supply Partnership</Eyebrow>
          <h1 className="font-heading text-hero font-extrabold uppercase tracking-wider mt-4">
            Become a <span className="text-cyan">Supplier</span>
          </h1>
          <p className="mt-6 text-dim text-lg max-w-2xl mx-auto">
            Partner with Optrenex to distribute your gaming products globally.
            Reach retailers and businesses across 10+ countries.
          </p>
          <div className="mt-8">
            <motion.a
              href="https://b2b.optrenex.com/register"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="supplier-register-btn"
              className="btn-primary inline-flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Register as Supplier <ArrowRight size={16} />
            </motion.a>
          </div>
        </motion.div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-panel/30">
        <div className="max-w-7xl mx-auto px-fluid">
          <div className="text-center mb-16">
            <h2 className="font-heading text-section font-bold uppercase tracking-wider">
              Why Supply Through <span className="text-cyan">Optrenex</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => (
              <StaggerReveal key={i} index={i}>
                <GlassCard className="h-full" data-testid={`supplier-benefit-${i}`}>
                  <div className="w-12 h-12 rounded-lg bg-cyan-soft flex items-center justify-center mb-5">
                    <b.icon size={24} className="text-cyan" />
                  </div>
                  <h3 className="font-ui text-lg font-semibold text-ink mb-3">{b.title}</h3>
                  <p className="text-dim text-sm leading-relaxed">{b.desc}</p>
                </GlassCard>
              </StaggerReveal>
            ))}
          </div>

          <StaggerReveal index={benefits.length}>
            <GlassCard className="mt-16 text-center">
              <h3 className="font-heading text-2xl font-bold uppercase tracking-wider text-ink mb-3">
                Ready to Expand Your Distribution?
              </h3>
              <p className="text-dim text-sm mb-6 max-w-lg mx-auto">
                Register on our B2B portal or email <a href="mailto:sourcing@optrenex.com" className="text-cyan hover:underline">sourcing@optrenex.com</a> to start the partnership process. Our team will review your application within 48 hours.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  href="https://b2b.optrenex.com/register"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="supplier-cta-register"
                  className="btn-primary inline-flex items-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Register Now <ArrowRight size={16} />
                </motion.a>
                <motion.a
                  href="https://b2b.optrenex.com/login"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="supplier-login-btn"
                  className="btn-secondary inline-flex items-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Supplier Login <ArrowRight size={16} />
                </motion.a>
              </div>
            </GlassCard>
          </StaggerReveal>
        </div>
      </section>
    </main>
  );
}
